<script setup lang="ts">
import type { InvalidApiKeyErrorEventDetail } from '@/shared/lib/utils/errorHandler'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { RouterView, useRoute } from 'vue-router'
import { refreshAccessToken } from '@/shared/api/http'
import { addAuthBreadcrumb } from '@/shared/api/reportApiError'
import { getAuthTokens, useCustomToast } from '@/shared/lib'
import {
  INVALID_API_KEY_ERROR_EVENT,
  WB_TOKEN_ERROR_EVENT,
} from '@/shared/lib/utils/errorHandler'
import CApiErrorHandler from '@/shared/ui/CApiErrorHandler.vue'
import CUpdateApiKeyModal from '@/shared/ui/CUpdateApiKeyModal.vue'

import { LDefault, LLanding } from './layouts'

const layouts = {
  default: LDefault,
  landing: LLanding,
}

const route = useRoute()
const { t } = useI18n()
const { error: showError } = useCustomToast()

const detectLayout = computed(() => {
  const metaLayout = route.meta.layout as keyof typeof layouts
  return layouts[metaLayout]
})

// Handle server errors globally
function handleServerError(_event: Event) {
  showError({
    title: 'api_server_error_title',
    message: 'api_server_error_message',
  })
}

// Handle wb token errors globally
const showUpdateApiKeyModal = ref(false)

function handleWbTokenError(_event: Event) {
  // Show error toast notification
  showError({
    title: t('wb_token_expired'),
    message: t('wb_token_expired_message'),
  })

  // Show the API key update modal
  showUpdateApiKeyModal.value = true
}

function handleInvalidApiKeyError(event: Event) {
  const customEvent = event as CustomEvent<InvalidApiKeyErrorEventDetail>

  // Show error toast notification with the specific message from the API
  showError({
    title: customEvent.detail.title,
    message: customEvent.detail.message,
  })
}

// Handle visibility change (wake from sleep)
// This proactively refreshes the access token when the page becomes visible.
//
// Reuses the shared `refreshAccessToken` so we get the same retry-vs-logout
// semantics: a transient 5xx / network error here will NOT crash the session,
// it just leaves the access token expired for the next request to handle.
let isRefreshing = false
async function handleVisibilityChange() {
  if (document.visibilityState !== 'visible')
    return
  if (isRefreshing)
    return

  const { accessToken, refreshToken } = getAuthTokens()

  // Only refresh if we have a refresh token but no access token (expired)
  if (accessToken || !refreshToken)
    return

  isRefreshing = true
  try {
    addAuthBreadcrumb('visibility_refresh_attempt')
    const result = await refreshAccessToken()

    // Reload only on success. A transient failure leaves things as-is so
    // the user keeps their navigation state; the next API call will retry.
    if (result.kind === 'ok') {
      addAuthBreadcrumb('visibility_refresh_success')
      window.location.reload()
    }
    else {
      addAuthBreadcrumb('visibility_refresh_skipped', { kind: result.kind })
    }
  }
  catch (error) {
    console.error('Failed to refresh token on visibility change:', error)
  }
  finally {
    isRefreshing = false
  }
}

onMounted(async () => {
  // Add global server error listener
  window.addEventListener('api-server-error', handleServerError)

  // Add global wb token error listener
  window.addEventListener(WB_TOKEN_ERROR_EVENT, handleWbTokenError)

  // Add global invalid API key error listener
  window.addEventListener(INVALID_API_KEY_ERROR_EVENT, handleInvalidApiKeyError)

  // Add visibility change listener to handle wake from sleep
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  // Remove global server error listener
  window.removeEventListener('api-server-error', handleServerError)

  // Remove global wb token error listener
  window.removeEventListener(WB_TOKEN_ERROR_EVENT, handleWbTokenError)

  // Remove global invalid API key error listener
  window.removeEventListener(
    INVALID_API_KEY_ERROR_EVENT,
    handleInvalidApiKeyError,
  )

  // Remove visibility change listener
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<template>
  <RouterView v-slot="{ Component }">
    <Transition mode="out-in" name="layout">
      <div :key="String(detectLayout)">
        <component :is="detectLayout">
          <Transition mode="out-in" name="page">
            <Component :is="Component" />
          </Transition>
        </component>
      </div>
    </Transition>

    <CApiErrorHandler />
    <CUpdateApiKeyModal v-model="showUpdateApiKeyModal" />
  </RouterView>
</template>
